import { popoverMotion } from "../../../design-system/tokens/popoverMotion";
import styled, { css, keyframes } from "styled-components";
import { duration, easing } from "../../../design-system/tokens/transitions";
import { borderRadius, fontSizes, rawColors, rawColors as uiColors } from "@magicbi/ui-library";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderWidth, borderStyle } from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { zIndex } from "../../../design-system/tokens/shadows";
import type { StoryHeaderNavIconTone } from "./StoryHeaderActions.types";
import { SHARE_ICON_TILE, SHARE_MENU_SHADOW, SHARE_MENU_WIDTH } from "./StoryHeaderActions.constants";

/** Figma 3812:41996 — right-side action group, gap 16px. */
export const Container = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
`;

const PillBase = styled.button`
  display: inline-flex;
  align-items: center;
  height: 40px;
  /* Figma pl-16 pr-12 py-8. */
  padding: ${spacing[2]} ${spacing[3]} ${spacing[2]} ${spacing[4]};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  box-sizing: border-box;

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

/** Collaborators trigger (F07): a bare avatar stack, no box, count or chevron. */
export const ProfilePill = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0 ${spacing[2]};
  height: ${spacing[8]};
  background: transparent;
  border: none;
  border-radius: ${borderRadius.full};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

/**
 * 28px avatar placeholder. Figma uses a masked user photo; rendered here as a
 * tinted circle with a user glyph until real collaborator avatars are wired.
 */
export const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  /* FA-16: stacked avatars overlap by 8px with a white ring. */
  box-shadow: 0 0 0 2px ${rawColors.structural.white};
  & + & {
    margin-left: -${spacing[2]};
  }
  flex-shrink: 0;
  border-radius: ${borderRadius.full};
  background: ${rawColors.primary[100]};
  color: ${rawColors.primary[500]};
  /* GAP: 11px initials have no type token (caption is 12). */
  font-size: 0.6875rem;
  font-weight: 700;
`;

/** Share pill (F07): primary 500 fill, full radius, white text and chevron; primary 200 when locked. */
export const SharePill = styled(PillBase)`
  /* FA-16: 34px tall, padding 0 16, chevron after the label. */
  gap: ${spacing[2]};
  height: 2.125rem;
  padding: 0 ${spacing[4]};
  border-radius: ${borderRadius.full};
  background: ${rawColors.primary[500]};
  border: none;

  /* Same disabled treatment as CanvasActionBar's DisabledBarOverlay. */
  &:disabled {
    /* FA-16: locked pill uses primary 200. */
    background: ${rawColors.primary[200]};
    cursor: not-allowed;
  }
`;

export const ShareText = styled.span`
  ${typographyMixin("body", 3)}
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${rawColors.text.white};
`;

/** Overflow button (F07): 32px round ghost, horizontal "more" glyph. */
export const KebabButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* FA-16: 32px kebab. */
  width: ${spacing[8]};
  height: ${spacing[8]};
  border-radius: ${borderRadius.full};
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${rawColors.text.mediumEmphasis};

  &:hover { background: ${rawColors.grey[100]}; }
  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

/** Refresh dropdown trigger (F07): a single 32px round ghost icon button. */
export const RefreshTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[8]};
  height: ${spacing[8]};
  padding: 0;
  border: none;
  border-radius: ${borderRadius.full};
  background: transparent;
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;

  &:hover { background: ${rawColors.grey[100]}; }
  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
`;

/**
 * Portal-rendered dropdown menu — Figma node 3731:230507.
 * Shadow: Figma "Drop Shadow/lg" (token gap vs codebase — exact Figma values used).
 */
export const NavMenuPortal = styled.ul`
  position: fixed;
  ${popoverMotion()}
  list-style: none;
  margin: 0;
  padding: 0 0 ${spacing[2]} 0;
  min-width: 180px;
  background: ${rawColors.structural.white};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  /* Figma Drop Shadow/lg — token gap: codebase lg shadow differs */
  box-shadow: 0px 10px 8px 0px rgba(0, 0, 0, 0.04),
    0px 4px 3px 0px rgba(0, 0, 0, 0.1);
  z-index: ${zIndex.dropdown};
  overflow: hidden;
  box-sizing: border-box;
`;

/** Figma list wrapper: pt 8px, px 8px — wraps each item group. */
export const NavMenuList = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${spacing[2]};
  padding-left: ${spacing[2]};
  padding-right: ${spacing[2]};
  width: 100%;
`;

/** Figma _dropdown-item: px 12px, py 8px, gap 12px, border-radius 8px. */
export const NavMenuItem = styled.li<{ $highlight?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
  white-space: nowrap;
  list-style: none;

  &:hover,
  &:focus-visible {
    background: ${rawColors.grey[50]};
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: -2px;
  }

  ${({ $highlight, theme }) =>
    $highlight &&
    css`
      background: ${theme.colors.background.tertiary};

      &:hover {
        background: ${theme.colors.background.tertiary};
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: default;
      opacity: 0.6;

      &:hover {
        background: none;
      }
    `}
`;

/** A hairline between menu groups. */
export const NavMenuDivider = styled.div`
  height: 1px;
  margin: ${spacing[2]} ${spacing[3]} 0;
  background: ${({ theme }) => theme.colors.border.subtle};
`;

/** End of a row that opens another screen. */
export const NavMenuTrailing = styled.span`
  display: inline-flex;
  flex: none;
  margin-left: auto;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    width: ${spacing[4]};
    height: ${spacing[4]};
  }
`;

/**
 * Wraps a leading `Switch` (sm track is 40px) in a switch-style dropdown row.
 * Width is explicit so it matches `NavMenuControlIcon` below and every row's
 * label lands on the same left edge in a menu that mixes icon and switch rows.
 */
export const NavMenuSwitch = styled.span`
  display: inline-flex;
  align-items: center;
  width: 40px;
  flex-shrink: 0;
`;

/** Figma icon slot: exactly 24×24, never shrinks. */
export const NavMenuIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

/**
 * `NavMenuIcon` widened to match `NavMenuSwitch`'s 40px — used only in
 * dropdowns (the refresh menu) that mix icon rows with a switch row, so
 * every row's label starts at the same x regardless of which control leads.
 */
export const NavMenuControlIcon = styled(NavMenuIcon)`
  width: 40px;
`;

/** Label + optional badge + description, for menu rows that explain themselves (Share menu). */
export const NavMenuText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 18rem;
  white-space: normal;
`;

export const NavMenuLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  max-width: 100%;
  white-space: nowrap;
  ${typographyMixin("body", 2)}
  font-size: 0.8125rem;
  font-weight: 700;
`;

export const NavMenuBadge = styled.span<{ $solid?: boolean }>`
  flex: none;
  padding: 0 ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${rawColors.primary[100]};
  color: ${rawColors.primary[500]};
  ${typographyMixin("caption", 2)}

  ${({ $solid, theme }) =>
    $solid &&
    css`
      padding: 0 ${spacing[1.5]};
      border-radius: ${borderRadius.sm};
      background: ${theme.colors.interactive.primary};
      color: ${rawColors.text.white};
      font-size: ${fontSizes["overline-4"]};
      font-weight: 700;
    `}
`;

export const NavMenuDescription = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

/** A live line under a share row's description (round 6: distributions set up, sending, needing attention). */
const noteFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** Fades in (duration.fast, 150ms) whenever its text changes; the caller keys it on the text. Off for reduced motion. */
export const NavMenuNote = styled.span`
  animation: ${noteFadeIn} ${duration.fast} ${easing.smooth} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  ${typographyMixin("caption", 2)}
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** One part of a note; `attention` is amber text. */
export const NavMenuNoteSegment = styled.span<{ $attention?: boolean }>`
  ${({ $attention }) =>
    $attention &&
    css`
      color: ${uiColors.accent.amber700};
    `}
`;

/** Undo / redo teaser group (disabled when nothing to undo/redo). */
export const HistoryGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
`;

export const HistoryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[8]};
  height: ${spacing[8]};
  padding: 0;
  background: transparent;
  border: none;
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;
  border-radius: ${borderRadius.full};

  &:disabled {
    color: ${rawColors.stroke.strong};
    cursor: not-allowed;
  }
`;

/** "+N" overflow chip closing the avatar stack. */
export const OverflowChip = styled(Avatar)`
  background: ${rawColors.grey[100]};
  color: ${rawColors.text.mediumEmphasis};
  font-size: 0.6875rem;
  font-weight: 600;
`;

export const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: ${borderRadius.full};
  object-fit: cover;
`;

/** Share menu (F17): 300 wide, padding 6, radius 14, 1px stroke, soft ink shadow. */
export const ShareMenuPortal = styled(NavMenuPortal)`
  width: ${SHARE_MENU_WIDTH};
  min-width: ${SHARE_MENU_WIDTH};
  padding: ${spacing[1.5]};
  border-radius: ${borderRadius[14]};
  box-shadow: ${SHARE_MENU_SHADOW};
`;

/** Share row: padding 10, radius 10, gap 12, top-aligned; the hover row is grey 50. */
export const ShareMenuItem = styled(NavMenuItem)`
  align-items: flex-start;
  padding: ${spacing[2.5]};
  border-radius: ${borderRadius[10]};
  white-space: normal;
`;

/** 1px stroke divider above a share row (F17: margin 4 0). */
export const ShareMenuDivider = styled.div`
  height: ${borderWidth[1]};
  margin: ${spacing[1]} 0;
  background: ${rawColors.stroke[200]};
`;

/** 30px icon tile: radius 9; accent is primary 100 fill with a primary 500 glyph, neutral is grey 100 with a strong glyph. */
export const ShareIconTile = styled.span<{ $tone?: StoryHeaderNavIconTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${SHARE_ICON_TILE};
  height: ${SHARE_ICON_TILE};
  border-radius: ${borderRadius[9]};
  background: ${({ $tone }) => ($tone === "neutral" ? rawColors.grey[100] : rawColors.primary[100])};
  color: ${({ $tone }) => ($tone === "neutral" ? rawColors.text.strongEmphasis : rawColors.primary[500])};

  svg {
    width: ${spacing[4]};
    height: ${spacing[4]};
  }
`;
